/**
 * Who carries this account or project, rendered so it cannot be read as a
 * stored fact.
 *
 * One component for the lists and both detail headers, because the three
 * states it has to distinguish are exactly the three a second implementation
 * would get wrong: no partner, a contested partner, and a partner inherited
 * from the account. See clientPartner.ts for why each one matters.
 */
import { TriangleAlert } from "lucide-react";
import {
  clientPartnerEvidence,
  clientPartnerRivals,
  INHERITED_HELP,
  UNASSIGNED,
  UNASSIGNED_HELP,
} from "@/components/profitability/clientPartner";
import type { ClientPartner } from "@/lib/profitability-api";
import { cn } from "@/lib/utils";

export function ClientPartnerCell({
  owner,
  className,
}: {
  owner: ClientPartner | null | undefined;
  className?: string;
}) {
  if (!owner) {
    // Quiet, but a word. 15 of 116 revenue-bearing accounts land here, and an
    // empty cell in a column of names reads as a row that failed to load.
    return (
      <span className={cn("text-muted-foreground", className)} title={UNASSIGNED_HELP}>
        {UNASSIGNED}
      </span>
    );
  }

  const rivals = clientPartnerRivals(owner);

  return (
    <span className={cn("inline-flex min-w-0 items-center gap-1.5", className)}>
      <span className="truncate" title={clientPartnerEvidence(owner)}>
        {owner.person_name}
      </span>
      {rivals ? (
        // Small on purpose. Only 2 accounts are contested today; a loud badge
        // on two rows out of 116 trains everyone to stop reading the marker,
        // which costs more than it buys. The rival names live in the title.
        <span
          role="img"
          aria-label={rivals}
          title={rivals}
          className="inline-flex shrink-0 items-center text-amber-600 dark:text-amber-400"
        >
          <TriangleAlert className="size-3" aria-hidden />
        </span>
      ) : null}
      {owner.inherited_from_account ? (
        // 2,581 of 5,218 project rows inherit. That is the normal case, so it
        // is a muted aside rather than anything that looks like a defect.
        <span className="shrink-0 text-xs text-muted-foreground" title={INHERITED_HELP}>
          via account
        </span>
      ) : null}
    </span>
  );
}
